import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Building2 } from 'lucide-react';
import { useAuth } from './AuthContext';
import './OrgSwitcher.css';

const OrgSwitcher = () => {
  const { user, setActiveEstablishment, getAccessToken } = useAuth();
  const [establishments, setEstablishments] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Only render for MultiClientAdmin with multiple establishments
  const isMultiClientAdmin = user?.role === 'MultiClientAdmin';
  const estIds = user?.establishment_ids || [];

  // Fetch establishment names on mount
  useEffect(() => {
    if (!isMultiClientAdmin || estIds.length === 0) return;
    const token = getAccessToken();
    if (!token) return;
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
    fetch(`${API_BASE_URL}/api/establishment`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        const all = data?.establishments || [];
        // Only show the ones this user is assigned to
        const assigned = all.filter(e => estIds.includes(e.id));
        setEstablishments(assigned);
      })
      .catch(() => {});
  }, [isMultiClientAdmin, estIds.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!isMultiClientAdmin) return null;

  const activeEst = establishments.find(e => e.id === user.activeEstablishmentId);
  const activeLabel = activeEst?.name || (user.activeEstablishmentId ? `Org #${user.activeEstablishmentId}` : 'Select Organization');

  return (
    <div className="org-switcher" ref={ref}>
      <button
        className="org-switcher-btn"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        aria-label="Switch organization"
        title="Switch organization"
      >
        <Building2 size={14} className="org-switcher-icon" />
        <span className="org-switcher-label">{activeLabel}</span>
        <ChevronDown size={13} className={`org-switcher-chevron ${open ? 'open' : ''}`} />
      </button>

      {open && (
        <div className="org-switcher-menu" role="menu">
          <div className="org-switcher-header">Switch Organization</div>
          {establishments.length === 0 ? (
            <div className="org-switcher-empty">No organizations assigned</div>
          ) : (
            establishments.map(est => (
              <button
                key={est.id}
                className={`org-switcher-item ${est.id === user.activeEstablishmentId ? 'active' : ''}`}
                role="menuitem"
                onClick={() => {
                  setActiveEstablishment(est.id);
                  setOpen(false);
                }}
              >
                <Building2 size={13} />
                <span>{est.name}</span>
                {est.id === user.activeEstablishmentId && (
                  <span className="org-switcher-check">✓</span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default OrgSwitcher;
